// Collection type detection utility
const CollectionTypeHelper = {
  coffeeCollections: [
    "coffee",
    "beans",
    "filter-coffee",
    "decaf",
    "espresso-bonen",
    "filter-bonen",
  ],
  equipmentCollections: [
    "espresso-apparatuur",
    "espresso-equipment",
    "espresso-grinders",
    "filter-equipment-mm",
    "filter-apparatuur",
    "filter-equipment",
    "equipment",
  ],
  brandCollections: [
    "kinto",
    "sage",
    "hario",
    "timemore",
    "barista-essentials",
    "fellow",
    "moccamaster",
    "origami",
    "kalita",
    "aeropress",
    "cafetto",
  ],

  getCurrentCollection() {
    const pathParts = window.location.pathname.split("/");
    const collectionsIndex = pathParts.indexOf("collections");
    if (collectionsIndex !== -1 && pathParts.length > collectionsIndex + 1) {
      return pathParts[collectionsIndex + 1].toLowerCase();
    }
    return "";
  },

  isCoffeeOrEquipmentCollection() {
    const currentCollection = this.getCurrentCollection();
    return (
      this.coffeeCollections.includes(currentCollection) ||
      this.equipmentCollections.includes(currentCollection)
    );
  },

  isBrandCollection() {
    const currentCollection = this.getCurrentCollection();
    return this.brandCollections.includes(currentCollection);
  },

  shouldUseClientSideFiltering() {
    return this.isCoffeeOrEquipmentCollection();
  },

  shouldHideOtherProductsSection() {
    return this.isBrandCollection();
  },

  shouldCompletelyHideOtherProducts() {
    // For all brands except KINTO, completely hide (display: none) the OTHER PRODUCTS section
    const currentCollection = this.getCurrentCollection();
    return this.isBrandCollection() && currentCollection !== "kinto";
  },
};

class CustomFilter extends HTMLElement {
  constructor() {
    super();
    this.slider =
      this.querySelector(".custom-filter__slider") ||
      this.querySelector(".mobile-custom-filter__slider");
    this.currentValueDisplay =
      this.querySelector(".custom-filter__current-value") ||
      this.querySelector(".mobile-custom-filter__current-value");
    this.labels =
      this.querySelectorAll(".custom-filter__label") ||
      this.querySelectorAll(".mobile-custom-filter__label");
    this.resetButton =
      this.querySelector(".custom-filter__reset") ||
      this.querySelector(".mobile-custom-filter__reset");
    this.clearButton = this.querySelector(".mobile-custom-filter__clear");

    // Filter steps: 0 = None, 1 = Low, 2 = Medium, 3 = High, 4 = Very High
    this.filterSteps = ["None", "Low", "Medium", "High", "Very High"];
    this.filterValues = ["", "low", "medium", "high", "very-high"];

    this.init();
  }

  init() {
    if (!this.slider) {
      console.warn("CustomFilter: No slider found for", this);
      return;
    }

    // Set initial state
    this.updateDisplay();

    // Add event listeners
    this.slider.addEventListener("input", this.handleSliderChange.bind(this));
    this.slider.addEventListener("change", this.handleSliderCommit.bind(this));

    if (this.resetButton) {
      this.resetButton.addEventListener("click", this.handleReset.bind(this));
    }

    if (this.clearButton) {
      this.clearButton.addEventListener("click", this.handleReset.bind(this));
    }

    // Initialize from URL parameters
    this.initializeFromURL();

    // Initialize flavor profile icon - ensure empty icon shows when at step 0
    const currentStep = parseInt(this.slider.value);
    this.updateFlavorProfileIcon(currentStep);
    
    // Ensure icon is updated even if DOM elements load later
    setTimeout(() => {
      this.updateFlavorProfileIcon(currentStep);
    }, 100);

    console.log(
      "CustomFilter initialized:",
      this.getAttribute("data-metafield-key"),
      "at step:",
      currentStep
    );
  }

  handleSliderChange(event) {
    const value = parseInt(event.target.value);
    this.updateDisplay(value);
    this.updateFlavorProfileIcon(value);

    // Debounce the filter application for better performance
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    this.filterTimeout = setTimeout(() => {
      this.applyFilter(value);
    }, 150); // Reduced from immediate to 150ms debounce
  }

  handleSliderCommit(event) {
    const value = parseInt(event.target.value);
    this.updateDisplay(value);
    this.applyFilter(value);
    this.updateFlavorProfileIcon(value);
  }

  updateDisplay(value = null) {
    if (value === null) {
      value = parseInt(this.slider.value);
    }

    // Update current value display
    if (this.currentValueDisplay) {
      this.currentValueDisplay.textContent = this.filterSteps[value];
    }

    // Update label states
    this.labels.forEach((label, index) => {
      label.classList.toggle("active", index === value);
    });

    // Show/hide reset button
    if (this.resetButton) {
      this.resetButton.classList.toggle("hidden", value === 0);
    }
  }

  applyFilter(value) {
    try {
      const filterValue = this.filterValues[value];
      const metafieldKey =
        this.getAttribute("data-metafield-key") || "intensity";

      // Update URL without page refresh
      this.updateURL(metafieldKey, filterValue);

      // Apply combined filtering for all active filters
      this.filterAllProducts();

      // Update product count
      this.updateProductCount();

      // Hide loading skeleton after filtering completes
      if (window.hideLoadingSkeleton) {
        setTimeout(() => {
          window.hideLoadingSkeleton();
        }, 100);
      }
    } catch (error) {
      console.error("Error applying filter:", error);
      // Restore products if filtering fails
      restoreProducts();
    }
  }

  filterAllProducts() {
    // Skip filtering if we're on a brand collection (navigation mode)
    if (CollectionTypeHelper.isBrandCollection()) {
      console.log("CustomFilter: Skipping filtering on brand collection page");
      return;
    }

    // Backup original products before filtering
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) {
      console.warn("CustomFilter: No product grid found");
      return;
    }

    // Store original products as backup
    if (!window.originalProducts) {
      const productItems = productGrid.querySelectorAll(
        ".grid__item[data-product-id]"
      );
      window.originalProducts = Array.from(productItems).map(
        (item) => item.outerHTML
      );
    }

    // Show loading skeleton during filtering
    if (window.showLoadingSkeleton) {
      window.showLoadingSkeleton();
    }

    // Hide all hover overlays during filtering to prevent flash
    if (window.hideAllHoverOverlays) {
      window.hideAllHoverOverlays();
    }

    // Safety check: ensure we have products before filtering
    const productItems = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    if (productItems.length === 0) {
      console.warn("CustomFilter: No products found in grid, skipping filter");
      return;
    }

    let filteredCount = 0;

    // Get all active filters
    const allFilters = document.querySelectorAll("custom-filter");
    const activeFilters = [];

    allFilters.forEach((filter) => {
      const slider =
        filter.querySelector(".custom-filter__slider") ||
        filter.querySelector(".mobile-custom-filter__slider");
      if (!slider) return;

      const metafieldKey = filter.getAttribute("data-metafield-key");
      const filterValue = filter.filterValues[parseInt(slider.value)];

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
        });
      }
    });

    // Separate products into filtered and other products
    const filteredProducts = [];
    const otherProducts = [];

    productItems.forEach((item) => {
      let isFiltered = false;

      // If no filters are active, all products are considered "other"
      if (activeFilters.length === 0) {
        otherProducts.push(item);
      } else {
        // Check if product matches ANY of the active filters (OR logic)
        for (let filter of activeFilters) {
          const dataAttributeName = `data-${filter.metafieldKey.replace(/_/g, "-")}`;
          let productMetafieldValue = item.getAttribute(dataAttributeName);
          
          // Normalize values for comparison (trim and lowercase)
          if (productMetafieldValue) {
            productMetafieldValue = productMetafieldValue.trim().toLowerCase();
          }
          const normalizedFilterValue = filter.filterValue ? filter.filterValue.trim().toLowerCase() : '';

          // If product matches this filter, it's filtered (OR logic - match any)
          if (productMetafieldValue === normalizedFilterValue) {
            isFiltered = true;
            break;
          }
        }

        if (isFiltered) {
          filteredProducts.push(item);
          filteredCount++;
        } else {
          otherProducts.push(item);
        }
      }
    });

    // Create a container for the new structure
    const container = document.createElement("div");
    container.className = "filtered-products-container";

    // Add filtered products section if there are filtered products
    if (filteredProducts.length > 0) {
      const filteredSection = document.createElement("div");
      filteredSection.className = "filtered-products-section";
      filteredSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <ul class="filtered-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const filteredGrid = filteredSection.querySelector(
        ".filtered-products-grid"
      );
      filteredProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        filteredGrid.appendChild(item);
      });

      container.appendChild(filteredSection);
    } else if (activeFilters.length > 0) {
      // Show no results message when filters are active but no products match
      const noResultsSection = document.createElement("div");
      noResultsSection.className =
        "filtered-products-section no-results-section";
      noResultsSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <div class="no-results-message" style="text-align: center; padding: 2rem 1rem; color: #666;">
          No products found matching your filters
        </div>
      `;

      container.appendChild(noResultsSection);
    }

    // Add other products section
    // Only show for: non-brand collections OR KINTO (hide for all other brands)
    const currentCollection = CollectionTypeHelper.getCurrentCollection();
    const shouldShowOtherProducts =
      !CollectionTypeHelper.isBrandCollection() ||
      currentCollection === "kinto";

    if (otherProducts.length > 0 && shouldShowOtherProducts) {
      const otherSection = document.createElement("div");
      otherSection.className = "other-products-section";
      otherSection.innerHTML = `
        <div class="other-products-header">
          <h3 class="other-products-title">PRODUCTS</h3>
        </div>
        <ul class="other-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const otherGrid = otherSection.querySelector(".other-products-grid");
      otherProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        otherGrid.appendChild(item);
      });

      container.appendChild(otherSection);
    }

    // Replace the product grid content
    productGrid.innerHTML = "";
    productGrid.appendChild(container);

    // Hide empty state since we're always showing products
    this.toggleEmptyState(false);
  }

  toggleEmptyState(isEmpty) {
    const productGrid = document.getElementById("product-grid");
    const collection = productGrid?.closest(".collection");

    if (!collection) return;

    let emptyState = collection.querySelector(".custom-filter-empty-state");

    if (isEmpty) {
      if (!emptyState) {
        emptyState = document.createElement("div");
        emptyState.className = "custom-filter-empty-state";
        emptyState.innerHTML = `
          <div style="text-align: center; padding: 2rem 1rem; color: #666;">
            No results found
          </div>
        `;
        collection.appendChild(emptyState);
      }
      emptyState.style.display = "block";
    } else {
      if (emptyState) {
        emptyState.style.display = "none";
      }
    }
  }

  updateProductCount() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const allProducts = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    const filteredProducts = productGrid.querySelectorAll(
      ".filtered-products-grid .grid__item[data-product-id]"
    );
    const otherProducts = productGrid.querySelectorAll(
      ".other-products-grid .grid__item[data-product-id]"
    );

    // Update product count displays to show total products
    const countElements = document.querySelectorAll(
      "#ProductCount, #ProductCountDesktop"
    );
    countElements.forEach((element) => {
      if (element) {
        const currentText = element.textContent;
        const newText = currentText.replace(/\d+/, allProducts.length);
        element.textContent = newText;
      }
    });

    // Remove any existing filter info
    document.querySelectorAll(".filter-info").forEach((info) => info.remove());
  }

  updateURL(metafieldKey, filterValue) {
    const url = new URL(window.location);
    const paramName = `custom_filter_${metafieldKey}`;

    if (filterValue && filterValue !== "") {
      url.searchParams.set(paramName, filterValue);
    } else {
      url.searchParams.delete(paramName);
    }

    // Update URL without page refresh
    window.history.replaceState({}, "", url.toString());
  }

  initializeFromURL() {
    // Skip URL initialization if we're in navigation mode (not coffee/equipment collection)
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      return;
    }

    const url = new URL(window.location);
    const metafieldKey = this.getAttribute("data-metafield-key") || "intensity";
    const paramName = `custom_filter_${metafieldKey}`;
    const currentValue = url.searchParams.get(paramName);

    if (currentValue) {
      const valueIndex = this.filterValues.indexOf(currentValue);
      if (valueIndex !== -1) {
        this.slider.value = valueIndex;
        this.updateDisplay(valueIndex);
        // Apply filter immediately - no setTimeout needed
        this.applyFilter(valueIndex);
      }
    }
  }

  handleReset(event) {
    event.preventDefault();
    this.slider.value = 0;
    this.updateDisplay(0);
    this.updateFlavorProfileIcon(0); // Update icon first to show empty state
    this.applyFilter(0);
  }

  updateFlavorProfileIcon(step) {
    const metafieldKey = this.getAttribute("data-metafield-key");
    const filterType = this.getFilterType(metafieldKey);
    const imageData = this.getImageDataForFilterType(filterType);
    const imageUrl = imageData[step] || imageData[0]; // Fallback to empty icon if step not found

    console.log(
      `Updating icon for ${filterType} (${metafieldKey}) to step ${step}, URL: ${imageUrl}`
    );

    // Update mobile flavor profile icon
    const mobileFlavorProfileItem = document.querySelector(
      '.mobile-flavor-profiles [data-filter="' + filterType + '"]'
    );
    if (mobileFlavorProfileItem) {
      const mobileIconContainer = mobileFlavorProfileItem.querySelector(
        ".mobile-flavor-profile-icon"
      );
      if (mobileIconContainer) {
        mobileIconContainer.innerHTML = `<img src="${imageUrl}" alt="${filterType} level ${step}" width="40" height="40">`;
      } else {
        console.warn(`Mobile icon container not found for ${filterType}`);
      }
    } else {
      console.warn(`Mobile flavor profile item not found for ${filterType}`);
    }

    // Update desktop flavor profile icon
    const desktopFlavorProfileItem = document.querySelector(
      '.desktop-flavor-profiles [data-filter="' + filterType + '"]'
    );
    if (desktopFlavorProfileItem) {
      const desktopIconContainer = desktopFlavorProfileItem.querySelector(
        ".desktop-flavor-profile-icon"
      );
      if (desktopIconContainer) {
        desktopIconContainer.innerHTML = `<img src="${imageUrl}" alt="${filterType} level ${step}" width="40" height="40">`;
      } else {
        console.warn(`Desktop icon container not found for ${filterType}`);
      }
    } else {
      console.warn(`Desktop flavor profile item not found for ${filterType}`);
    }
  }

  getImageDataForFilterType(filterType) {
    if (filterType === "fruity") {
      return {
        0: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Mask_group_7.svg?v=1755696692",
        1: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/fruitness-step-1.svg?v=1754557934",
        2: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/fruitness-step-2.svg?v=1754557934",
        3: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/fruitness-step-3.svg?v=1754557934",
        4: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/fruitness-step-4.svg?v=1754557933",
      };
    }

    if (filterType === "sweetness") {
      return {
        0: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Mask_group_6.svg?v=1755696688",
        1: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/zoetheid-step-1.svg?v=1754557032",
        2: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/zoetheid-step-2.svg?v=1754557032",
        3: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/zoetheid-step-3.svg?v=1754557032",
        4: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/zoetheid-step-4.svg?v=1754557031",
      };
    }

    if (filterType === "acidity") {
      return {
        0: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Mask_group_8.svg?v=1755696696",
        1: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/acidity-step-1.svg?v=1754558581",
        2: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/acidity-step-2.svg?v=1754558581",
        3: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/acidity-step-3.svg?v=1754558581",
        4: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/acidity-step-4.svg?v=1754558580",
      };
    }

    if (filterType === "vegetal") {
      return {
        0: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Group_28_2.svg?v=1755696701",
        1: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/vegetale-step-1.svg?v=1765558997",
        2: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/vegetale-step-2.svg?v=1765558997",
        3: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/vegetale-step-3.svg?v=1765558997",
        4: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/vegetale-step-4.svg?v=1765558997",
      };
    }

    // Default image data for other filter types
    return {
      0: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Sweetness_-_00.png?v=1754490711",
      1: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Sweetness_-_25.svg?v=1754490831",
      2: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Sweetness_-_50.png?v=1754490832",
      3: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Sweetness_-_75.png?v=1754490832",
      4: "https://cdn.shopify.com/s/files/1/0045/3961/3256/files/Sweetness_-_100.png?v=1754490832",
    };
  }

  getFilterType(metafieldKey) {
    // Map metafield keys to filter types
    const filterMap = {
      intensity: "sweetness",
      fruitig: "fruity",
      "aciditeit_filter": "acidity",
      kruidig: "vegetal",
    };
    return filterMap[metafieldKey] || "sweetness";
  }

  // Static method to initialize all custom filters
  static initializeAll() {
    try {
      // Prevent multiple initializations
      if (window.customFiltersInitialized) {
        console.log("Custom filters already initialized, skipping");
        return;
      }

      // Check if jQuery is available (for compatibility)
      if (typeof jQuery === "undefined") {
        console.warn(
          "jQuery not available, but continuing with custom filters"
        );
      }

      // Check if there are any active filters in URL before reorganizing
      const url = new URL(window.location);
      const hasActiveFilters = Array.from(url.searchParams.keys()).some(
        (key) => key.startsWith("custom_filter_")
      );

      // Only reorganize products if there are active filters in URL
      if (hasActiveFilters) {
        console.log("Active filters found in URL, organizing products...");
        const customFilters = document.querySelectorAll("custom-filter");
        if (customFilters.length > 0) {
          // Just trigger filtering on the first filter to update all products
          const firstFilter = customFilters[0];
          if (
            firstFilter &&
            typeof firstFilter.filterAllProducts === "function"
          ) {
            firstFilter.filterAllProducts();
            firstFilter.updateProductCount();
          }
        }
      } else {
        console.log("No active filters in URL, skipping product reorganization");
      }

      // Mark as initialized
      window.customFiltersInitialized = true;
    } catch (error) {
      console.error("Error initializing custom filters:", error);
      // Don't mark as initialized if there was an error
    }

    // Initialize equipment filters
    const equipmentFilters = document.querySelectorAll("equipment-filter");
    equipmentFilters.forEach((filter) => {
      if (filter.dropdown) {
        filter.initializeFromURL();
      }
    });

    // Initialize brand filters
    const brandFilters = document.querySelectorAll("brand-filter");
    brandFilters.forEach((filter) => {
      filter.initializeFromURL();
    });

    // Initialize workshop filters
    const workshopFilters = document.querySelectorAll("workshop-filter");
    workshopFilters.forEach((filter) => {
      if (filter.dropdown) {
        filter.initializeFromURL();
      }
    });
  }
}

// Equipment Filter Class
class EquipmentFilter extends HTMLElement {
  constructor() {
    super();
    this.optionsContainer = this.querySelector(
      ".equipment-filter__options-container"
    );
    this.options = this.querySelectorAll(".equipment-filter__option");
    this.optionLabels =
      this.getAttribute("data-option-labels")?.split("|||") || [];
    this.optionValues =
      this.getAttribute("data-option-values")?.split("|||") || [];

    this.init();
  }

  init() {
    // Check if this is a single-option filter with clickable title
    const clickableTitle = this.querySelector(
      ".equipment-filter__title--clickable"
    );
    if (clickableTitle) {
      // Single-option filter - add click listener to title
      clickableTitle.addEventListener(
        "click",
        this.handleTitleClick.bind(this)
      );
    } else if (this.optionsContainer) {
      // Multi-option filter - add event listeners to each option
      this.options.forEach((option) => {
        option.addEventListener("click", this.handleOptionClick.bind(this));
      });
    } else {
      console.warn(
        "EquipmentFilter: No options container or clickable title found for",
        this
      );
      return;
    }

    // Initialize from URL parameters
    this.initializeFromURL();

    console.log(
      "EquipmentFilter initialized:",
      this.getAttribute("data-metafield-key")
    );
  }

  handleOptionClick(event) {
    const clickedOption = event.currentTarget;
    const value = clickedOption.getAttribute("data-value");

    // Check if we should use client-side filtering or navigate to collection
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      // Navigate to the collection instead of filtering
      const collectionUrl =
        clickedOption.getAttribute("data-collection-url") ||
        `/collections/${value}`;
      window.location.href = collectionUrl;
      return;
    }

    // Check if the clicked option is already active
    if (clickedOption.classList.contains("active")) {
      // If already active, deselect it and clear the filter
      this.clearSelection();
      this.applyFilter("");
    } else {
      // If not active, select it and apply the filter
      this.updateOptionSelection(value);
      this.applyFilter(value);
    }
  }

  updateOptionSelection(selectedValue) {
    // Remove active class from all options
    this.options.forEach((option) => {
      option.classList.remove("active");
    });

    // Add active class to selected option
    const selectedOption = this.querySelector(
      `[data-value="${selectedValue}"]`
    );
    if (selectedOption) {
      selectedOption.classList.add("active");
    }
  }

  clearSelection() {
    // Remove active class from all options
    this.options.forEach((option) => {
      option.classList.remove("active");
    });
  }

  applyFilter(value) {
    const metafieldKey =
      this.getAttribute("data-metafield-key") || "equipment_type";

    // Update URL without page refresh
    this.updateURL(metafieldKey, value);

    // Apply combined filtering for all active filters
    this.filterAllProducts();

    // Update product count
    this.updateProductCount();

    // Hide loading skeleton after filtering completes
    if (window.hideLoadingSkeleton) {
      setTimeout(() => {
        window.hideLoadingSkeleton();
      }, 100);
    }
  }

  filterAllProducts() {
    // Skip filtering if we're on a brand collection (navigation mode)
    if (CollectionTypeHelper.isBrandCollection()) {
      console.log(
        "EquipmentFilter: Skipping filtering on brand collection page"
      );
      return;
    }

    // Show loading skeleton during filtering
    if (window.showLoadingSkeleton) {
      window.showLoadingSkeleton();
    }

    // Hide all hover overlays during filtering to prevent flash
    if (window.hideAllHoverOverlays) {
      window.hideAllHoverOverlays();
    }

    const productGrid = document.getElementById("product-grid");
    if (!productGrid) {
      console.warn("EquipmentFilter: No product grid found");
      return;
    }

    // Safety check: ensure we have products before filtering
    const productItems = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    if (productItems.length === 0) {
      console.warn(
        "EquipmentFilter: No products found in grid, skipping filter"
      );
      return;
    }

    console.log(
      "EquipmentFilter: Found",
      productItems.length,
      "products to filter"
    );
    let filteredCount = 0;

    // Get all active filters (both custom and equipment filters)
    const allCustomFilters = document.querySelectorAll("custom-filter");
    const allEquipmentFilters = document.querySelectorAll("equipment-filter");
    console.log(
      "EquipmentFilter: Found",
      allCustomFilters.length,
      "custom filters and",
      allEquipmentFilters.length,
      "equipment filters"
    );
    const activeFilters = [];

    // Process custom filters (sliders)
    allCustomFilters.forEach((filter) => {
      const slider =
        filter.querySelector(".custom-filter__slider") ||
        filter.querySelector(".mobile-custom-filter__slider");
      if (!slider) return;

      const metafieldKey = filter.getAttribute("data-metafield-key");
      const filterValue = filter.filterValues[parseInt(slider.value)];

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
        });
      }
    });

    // Process equipment filters (options and clickable titles)
    allEquipmentFilters.forEach((filter) => {
      const metafieldKey = filter.getAttribute("data-metafield-key");
      let filterValue = "";

      // Check for clickable title first
      const clickableTitle = filter.querySelector(
        ".equipment-filter__title--clickable.active"
      );
      if (clickableTitle) {
        filterValue = clickableTitle.getAttribute("data-value");
      } else {
        // Check for regular option
        const activeOption = filter.querySelector(
          ".equipment-filter__option.active"
        );
        if (activeOption) {
          filterValue = activeOption.getAttribute("data-value");
        }
      }

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
        });
      }
    });

    // Separate products into filtered and other products
    const filteredProducts = [];
    const otherProducts = [];

    productItems.forEach((item) => {
      let isFiltered = false;

      // If no filters are active, all products are considered "other"
      if (activeFilters.length === 0) {
        otherProducts.push(item);
      } else {
        // Check if product matches ANY of the active filters (OR logic)
        for (let filter of activeFilters) {
          const productMetafieldValue = item.getAttribute(
            `data-${filter.metafieldKey.replace(/_/g, "-")}`
          );

          // If product matches this filter, it's filtered
          if (productMetafieldValue === filter.filterValue) {
            isFiltered = true;
            break;
          }
        }

        if (isFiltered) {
          filteredProducts.push(item);
          filteredCount++;
        } else {
          otherProducts.push(item);
        }
      }
    });

    // Create a container for the new structure
    const container = document.createElement("div");
    container.className = "filtered-products-container";

    // Add filtered products section if there are filtered products
    if (filteredProducts.length > 0) {
      const filteredSection = document.createElement("div");
      filteredSection.className = "filtered-products-section";
      filteredSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <ul class="filtered-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const filteredGrid = filteredSection.querySelector(
        ".filtered-products-grid"
      );
      filteredProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        filteredGrid.appendChild(item);
      });

      container.appendChild(filteredSection);
    } else if (activeFilters.length > 0) {
      // Show no results message when filters are active but no products match
      const noResultsSection = document.createElement("div");
      noResultsSection.className =
        "filtered-products-section no-results-section";
      noResultsSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <div class="no-results-message" style="text-align: center; padding: 2rem 1rem; color: #666;">
          No products found matching your filters
        </div>
      `;

      container.appendChild(noResultsSection);
    }

    // Add other products section
    // Only show for: non-brand collections OR KINTO (hide for all other brands)
    const currentCollection = CollectionTypeHelper.getCurrentCollection();
    const shouldShowOtherProducts =
      !CollectionTypeHelper.isBrandCollection() ||
      currentCollection === "kinto";

    if (otherProducts.length > 0 && shouldShowOtherProducts) {
      const otherSection = document.createElement("div");
      otherSection.className = "other-products-section";
      otherSection.innerHTML = `
        <div class="other-products-header">
          <h3 class="other-products-title">PRODUCTS</h3>
        </div>
        <ul class="other-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const otherGrid = otherSection.querySelector(".other-products-grid");
      otherProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        otherGrid.appendChild(item);
      });

      container.appendChild(otherSection);
    }

    // Replace the product grid content
    productGrid.innerHTML = "";
    productGrid.appendChild(container);

    // Hide empty state since we're always showing products
    this.toggleEmptyState(false);
  }

  toggleEmptyState(isEmpty) {
    const productGrid = document.getElementById("product-grid");
    const collection = productGrid?.closest(".collection");

    if (!collection) return;

    let emptyState = collection.querySelector(".custom-filter-empty-state");

    if (isEmpty) {
      if (!emptyState) {
        emptyState = document.createElement("div");
        emptyState.className = "custom-filter-empty-state";
        emptyState.innerHTML = `
          <div style="text-align: center; padding: 2rem 1rem; color: #666;">
            No results found
          </div>
        `;
        collection.appendChild(emptyState);
      }
      emptyState.style.display = "block";
    } else {
      if (emptyState) {
        emptyState.style.display = "none";
      }
    }
  }

  updateProductCount() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const allProducts = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    const filteredProducts = productGrid.querySelectorAll(
      ".filtered-products-grid .grid__item[data-product-id]"
    );
    const otherProducts = productGrid.querySelectorAll(
      ".other-products-grid .grid__item[data-product-id]"
    );

    // Update product count displays to show total products
    const countElements = document.querySelectorAll(
      "#ProductCount, #ProductCountDesktop"
    );
    countElements.forEach((element) => {
      if (element) {
        const currentText = element.textContent;
        const newText = currentText.replace(/\d+/, allProducts.length);
        element.textContent = newText;
      }
    });

    // Remove any existing filter info
    document.querySelectorAll(".filter-info").forEach((info) => info.remove());
  }

  updateURL(metafieldKey, filterValue) {
    const url = new URL(window.location);
    const paramName = `custom_filter_${metafieldKey}`;

    if (filterValue && filterValue !== "") {
      url.searchParams.set(paramName, filterValue);
    } else {
      url.searchParams.delete(paramName);
    }

    // Update URL without page refresh
    window.history.replaceState({}, "", url.toString());
  }

  initializeFromURL() {
    // Skip URL initialization if we're in navigation mode (not coffee/equipment collection)
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      return;
    }

    const url = new URL(window.location);
    const metafieldKey =
      this.getAttribute("data-metafield-key") || "equipment_type";
    const paramName = `custom_filter_${metafieldKey}`;
    const currentValue = url.searchParams.get(paramName);

    if (currentValue) {
      // Check if this is a clickable title filter
      const clickableTitle = this.querySelector(
        ".equipment-filter__title--clickable"
      );
      if (clickableTitle) {
        this.updateTitleSelection(currentValue);
      } else {
        this.updateOptionSelection(currentValue);
      }
      // Apply filter immediately - no setTimeout needed
      this.applyFilter(currentValue);
    }
  }

  handleTitleClick(event) {
    const clickedTitle = event.currentTarget;
    const value = clickedTitle.getAttribute("data-value");
    const isActive = clickedTitle.classList.contains("active");

    // Check if we should use client-side filtering or navigate to collection
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      // Navigate to the collection instead of filtering
      const collectionUrl =
        clickedTitle.getAttribute("data-collection-url") ||
        `/collections/${value}`;
      window.location.href = collectionUrl;
      return;
    }

    // Toggle between active and inactive state
    if (isActive) {
      // Currently active, deactivate (set to empty value)
      this.updateTitleSelection("");
      this.applyFilter("");
    } else {
      // Currently inactive, activate (set to filter value)
      this.updateTitleSelection(value);
      this.applyFilter(value);
    }
  }

  updateTitleSelection(selectedValue) {
    const clickableTitle = this.querySelector(
      ".equipment-filter__title--clickable"
    );
    if (clickableTitle) {
      if (selectedValue === "") {
        clickableTitle.classList.remove("active");
      } else {
        clickableTitle.classList.add("active");
      }
    }
  }
}

// Brand Filter Class
class BrandFilter extends HTMLElement {
  constructor() {
    super();
    this.options = this.querySelectorAll(".brand-filter__option");
    this.resetButton = this.querySelector(".brand-filter__reset");
    this.metafieldKey = this.getAttribute("data-metafield-key") || "brand_name";

    this.init();
  }

  init() {
    if (!this.options.length) {
      console.warn("BrandFilter: No options found for", this);
      return;
    }

    // Add event listeners
    this.options.forEach((option) => {
      option.addEventListener("click", this.handleOptionClick.bind(this));
    });

    if (this.resetButton) {
      this.resetButton.addEventListener("click", this.handleReset.bind(this));
    }

    // Initialize from URL parameters
    this.initializeFromURL();

    console.log("BrandFilter initialized");
  }

  handleOptionClick(event) {
    const clickedOption = event.currentTarget;
    const value = clickedOption.getAttribute("data-value");
    const isActive = clickedOption.classList.contains("active");

    // Check if we should use client-side filtering or navigate to collection
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      // Navigate to the brand collection instead of filtering
      const collectionUrl =
        clickedOption.getAttribute("data-collection-url") ||
        `/collections/${value}`;
      window.location.href = collectionUrl;
      return;
    }

    // Toggle between active and inactive state
    if (isActive) {
      // Currently active, deactivate
      this.updateOptionSelection("");
      this.applyFilter();
    } else {
      // Currently inactive, activate
      this.updateOptionSelection(value);
      this.applyFilter();
    }
  }

  updateOptionSelection(selectedValue) {
    // Remove active class from all options
    this.options.forEach((option) => {
      option.classList.remove("active");
    });

    // Add active class to selected option if there is one
    if (selectedValue) {
      const selectedOption = this.querySelector(
        `[data-value="${selectedValue}"]`
      );
      if (selectedOption) {
        selectedOption.classList.add("active");
      }
    }
  }

  applyFilter() {
    const selectedBrands = this.getSelectedBrands();

    // Update URL without page refresh
    this.updateURL(selectedBrands);

    // Apply combined filtering for all active filters
    this.filterAllProducts();

    // Update product count
    this.updateProductCount();

    // Hide loading skeleton after filtering completes
    if (window.hideLoadingSkeleton) {
      setTimeout(() => {
        window.hideLoadingSkeleton();
      }, 100);
    }
  }

  getSelectedBrands() {
    const selectedBrands = [];
    this.options.forEach((option) => {
      if (option.classList.contains("active")) {
        selectedBrands.push(option.getAttribute("data-value"));
      }
    });
    return selectedBrands;
  }

  filterAllProducts() {
    // Skip filtering if we're on a brand collection (navigation mode)
    if (CollectionTypeHelper.isBrandCollection()) {
      console.log("BrandFilter: Skipping filtering on brand collection page");
      return;
    }

    // Show loading skeleton during filtering
    if (window.showLoadingSkeleton) {
      window.showLoadingSkeleton();
    }

    // Hide all hover overlays during filtering to prevent flash
    if (window.hideAllHoverOverlays) {
      window.hideAllHoverOverlays();
    }

    const productGrid = document.getElementById("product-grid");
    if (!productGrid) {
      console.warn("BrandFilter: No product grid found");
      return;
    }

    // Safety check: ensure we have products before filtering
    const productItems = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    if (productItems.length === 0) {
      console.warn("BrandFilter: No products found in grid, skipping filter");
      return;
    }

    console.log(
      "BrandFilter: Found",
      productItems.length,
      "products to filter"
    );

    // Get all active filters (custom, equipment, and brand filters)
    const allCustomFilters = document.querySelectorAll("custom-filter");
    const allEquipmentFilters = document.querySelectorAll("equipment-filter");
    const allBrandFilters = document.querySelectorAll("brand-filter");
    console.log(
      "BrandFilter: Found",
      allCustomFilters.length,
      "custom filters,",
      allEquipmentFilters.length,
      "equipment filters, and",
      allBrandFilters.length,
      "brand filters"
    );

    const activeFilters = [];

    // Process custom filters (sliders)
    allCustomFilters.forEach((filter) => {
      const slider =
        filter.querySelector(".custom-filter__slider") ||
        filter.querySelector(".mobile-custom-filter__slider");
      if (!slider) return;

      const metafieldKey = filter.getAttribute("data-metafield-key");
      const filterValue = filter.filterValues[parseInt(slider.value)];

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
          type: "metafield",
        });
      }
    });

    // Process equipment filters (options and clickable titles)
    allEquipmentFilters.forEach((filter) => {
      const metafieldKey = filter.getAttribute("data-metafield-key");
      let filterValue = "";

      // Check for clickable title first
      const clickableTitle = filter.querySelector(
        ".equipment-filter__title--clickable.active"
      );
      if (clickableTitle) {
        filterValue = clickableTitle.getAttribute("data-value");
      } else {
        // Check for regular option
        const activeOption = filter.querySelector(
          ".equipment-filter__option.active"
        );
        if (activeOption) {
          filterValue = activeOption.getAttribute("data-value");
        }
      }

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
          type: "metafield",
        });
      }
    });

    // Process brand filters (checkboxes)
    allBrandFilters.forEach((filter) => {
      const selectedBrands = filter.getSelectedBrands();
      if (selectedBrands.length > 0) {
        selectedBrands.forEach((brand) => {
          activeFilters.push({
            filterValue: brand,
            metafieldKey: filter.metafieldKey,
            type: "metafield",
          });
        });
      }
    });

    // Separate products into filtered and other products
    const filteredProducts = [];
    const otherProducts = [];

    productItems.forEach((item) => {
      let isFiltered = false;

      // If no filters are active, all products are considered "other"
      if (activeFilters.length === 0) {
        otherProducts.push(item);
      } else {
        // Check if product matches ANY of the active filters (OR logic)
        for (let filter of activeFilters) {
          if (filter.type === "metafield") {
            const productMetafieldValue = item.getAttribute(
              `data-${filter.metafieldKey.replace(/_/g, "-")}`
            );

            // If product matches this filter, it's filtered
            if (productMetafieldValue === filter.filterValue) {
              isFiltered = true;
              break;
            }
          }
        }

        if (isFiltered) {
          filteredProducts.push(item);
        } else {
          otherProducts.push(item);
        }
      }
    });

    // Create a container for the new structure
    const container = document.createElement("div");
    container.className = "filtered-products-container";

    // Add filtered products section if there are filtered products
    if (filteredProducts.length > 0) {
      const filteredSection = document.createElement("div");
      filteredSection.className = "filtered-products-section";
      filteredSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <ul class="filtered-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const filteredGrid = filteredSection.querySelector(
        ".filtered-products-grid"
      );
      filteredProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        filteredGrid.appendChild(item);
      });

      container.appendChild(filteredSection);
    } else if (activeFilters.length > 0) {
      // Show no results message when filters are active but no products match
      const noResultsSection = document.createElement("div");
      noResultsSection.className =
        "filtered-products-section no-results-section";
      noResultsSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <div class="no-results-message" style="text-align: center; padding: 2rem 1rem; color: #666;">
          No products found matching your filters
        </div>
      `;

      container.appendChild(noResultsSection);
    }

    // Add other products section
    // Only show for: non-brand collections OR KINTO (hide for all other brands)
    const currentCollection = CollectionTypeHelper.getCurrentCollection();
    const shouldShowOtherProducts =
      !CollectionTypeHelper.isBrandCollection() ||
      currentCollection === "kinto";

    if (otherProducts.length > 0 && shouldShowOtherProducts) {
      const otherSection = document.createElement("div");
      otherSection.className = "other-products-section";
      otherSection.innerHTML = `
        <div class="other-products-header">
          <h3 class="other-products-title">PRODUCTS</h3>
        </div>
        <ul class="other-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const otherGrid = otherSection.querySelector(".other-products-grid");
      otherProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        otherGrid.appendChild(item);
      });

      container.appendChild(otherSection);
    }

    // Replace the product grid content
    productGrid.innerHTML = "";
    productGrid.appendChild(container);

    // Hide empty state since we're always showing products
    this.toggleEmptyState(false);
  }

  toggleEmptyState(isEmpty) {
    const productGrid = document.getElementById("product-grid");
    const collection = productGrid?.closest(".collection");

    if (!collection) return;

    let emptyState = collection.querySelector(".custom-filter-empty-state");

    if (isEmpty) {
      if (!emptyState) {
        emptyState = document.createElement("div");
        emptyState.className = "custom-filter-empty-state";
        emptyState.innerHTML = `
          <div style="text-align: center; padding: 2rem 1rem; color: #666;">
            No results found
          </div>
        `;
        collection.appendChild(emptyState);
      }
      emptyState.style.display = "block";
    } else {
      if (emptyState) {
        emptyState.style.display = "none";
      }
    }
  }

  updateProductCount() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const allProducts = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    const filteredProducts = productGrid.querySelectorAll(
      ".filtered-products-grid .grid__item[data-product-id]"
    );
    const otherProducts = productGrid.querySelectorAll(
      ".other-products-grid .grid__item[data-product-id]"
    );

    // Update product count displays to show total products
    const countElements = document.querySelectorAll(
      "#ProductCount, #ProductCountDesktop"
    );
    countElements.forEach((element) => {
      if (element) {
        const currentText = element.textContent;
        const newText = currentText.replace(/\d+/, allProducts.length);
        element.textContent = newText;
      }
    });

    // Remove any existing filter info
    document.querySelectorAll(".filter-info").forEach((info) => info.remove());
  }

  updateURL(selectedBrands) {
    const url = new URL(window.location);
    const paramName = "custom_filter_brands";

    if (selectedBrands.length > 0) {
      url.searchParams.set(paramName, selectedBrands.join(","));
    } else {
      url.searchParams.delete(paramName);
    }

    // Update URL without page refresh
    window.history.replaceState({}, "", url.toString());
  }

  initializeFromURL() {
    // Skip URL initialization if we're in navigation mode (not coffee/equipment collection)
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      return;
    }

    const url = new URL(window.location);
    const paramName = "custom_filter_brands";
    const currentValue = url.searchParams.get(paramName);

    if (currentValue) {
      const selectedBrands = currentValue.split(",");
      // For single selection, only activate the first selected brand
      if (selectedBrands.length > 0 && selectedBrands[0]) {
        this.updateOptionSelection(selectedBrands[0]);
      }
      // Apply filter immediately - no setTimeout needed
      this.applyFilter();
    }
  }

  handleReset(event) {
    event.preventDefault();
    this.updateOptionSelection("");
    this.applyFilter();
  }
}

// Workshop Filter Class
class WorkshopFilter extends HTMLElement {
  constructor() {
    super();
    this.optionsContainer = this.querySelector(
      ".workshop-filter__options-container"
    );
    this.options = this.querySelectorAll(".workshop-filter__option");
    this.optionLabels =
      this.getAttribute("data-option-labels")?.split("|||") || [];
    this.optionValues =
      this.getAttribute("data-option-values")?.split("|||") || [];

    this.init();
  }

  init() {
    if (!this.optionsContainer) {
      console.warn("WorkshopFilter: No options container found for", this);
      return;
    }

    // Add event listeners to each option
    this.options.forEach((option) => {
      option.addEventListener("click", this.handleOptionClick.bind(this));
    });

    // Initialize from URL parameters
    this.initializeFromURL();

    console.log(
      "WorkshopFilter initialized:",
      this.getAttribute("data-metafield-key")
    );
  }

  handleOptionClick(event) {
    const clickedOption = event.currentTarget;
    const value = clickedOption.getAttribute("data-value");

    // Check if we should use client-side filtering or navigate to collection
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      // Navigate to the workshop collection instead of filtering
      const collectionUrl =
        clickedOption.getAttribute("data-collection-url") ||
        `/collections/${value}`;
      window.location.href = collectionUrl;
      return;
    }

    // Update visual state
    this.updateOptionSelection(value);

    // Apply filter
    this.applyFilter(value);
  }

  updateOptionSelection(selectedValue) {
    // Remove active class from all options
    this.options.forEach((option) => {
      option.classList.remove("active");
    });

    // Add active class to selected option
    const selectedOption = this.querySelector(
      `[data-value="${selectedValue}"]`
    );
    if (selectedOption) {
      selectedOption.classList.add("active");
    }
  }

  applyFilter(value) {
    const metafieldKey =
      this.getAttribute("data-metafield-key") || "workshop_type";

    // Update URL without page refresh
    this.updateURL(metafieldKey, value);

    // Apply combined filtering for all active filters
    this.filterAllProducts();

    // Update product count
    this.updateProductCount();

    // Hide loading skeleton after filtering completes
    if (window.hideLoadingSkeleton) {
      setTimeout(() => {
        window.hideLoadingSkeleton();
      }, 100);
    }
  }

  filterAllProducts() {
    // Skip filtering if we're on a brand collection (navigation mode)
    if (CollectionTypeHelper.isBrandCollection()) {
      console.log(
        "WorkshopFilter: Skipping filtering on brand collection page"
      );
      return;
    }

    // Show loading skeleton during filtering
    if (window.showLoadingSkeleton) {
      window.showLoadingSkeleton();
    }

    // Hide all hover overlays during filtering to prevent flash
    if (window.hideAllHoverOverlays) {
      window.hideAllHoverOverlays();
    }

    const productGrid = document.getElementById("product-grid");
    if (!productGrid) {
      console.warn("WorkshopFilter: No product grid found");
      return;
    }

    // Safety check: ensure we have products before filtering
    const productItems = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    if (productItems.length === 0) {
      console.warn(
        "WorkshopFilter: No products found in grid, skipping filter"
      );
      return;
    }

    console.log(
      "WorkshopFilter: Found",
      productItems.length,
      "products to filter"
    );

    // Get all active filters (custom, equipment, brand, and workshop filters)
    const allCustomFilters = document.querySelectorAll("custom-filter");
    const allEquipmentFilters = document.querySelectorAll("equipment-filter");
    const allBrandFilters = document.querySelectorAll("brand-filter");
    const allWorkshopFilters = document.querySelectorAll("workshop-filter");
    console.log(
      "WorkshopFilter: Found",
      allCustomFilters.length,
      "custom filters,",
      allEquipmentFilters.length,
      "equipment filters,",
      allBrandFilters.length,
      "brand filters, and",
      allWorkshopFilters.length,
      "workshop filters"
    );

    const activeFilters = [];

    // Process custom filters (sliders)
    allCustomFilters.forEach((filter) => {
      const slider =
        filter.querySelector(".custom-filter__slider") ||
        filter.querySelector(".mobile-custom-filter__slider");
      if (!slider) return;

      const metafieldKey = filter.getAttribute("data-metafield-key");
      const filterValue = filter.filterValues[parseInt(slider.value)];

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
          type: "metafield",
        });
      }
    });

    // Process equipment filters (options and clickable titles)
    allEquipmentFilters.forEach((filter) => {
      const metafieldKey = filter.getAttribute("data-metafield-key");
      let filterValue = "";

      // Check for clickable title first
      const clickableTitle = filter.querySelector(
        ".equipment-filter__title--clickable.active"
      );
      if (clickableTitle) {
        filterValue = clickableTitle.getAttribute("data-value");
      } else {
        // Check for regular option
        const activeOption = filter.querySelector(
          ".equipment-filter__option.active"
        );
        if (activeOption) {
          filterValue = activeOption.getAttribute("data-value");
        }
      }

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
          type: "metafield",
        });
      }
    });

    // Process brand filters (checkboxes)
    allBrandFilters.forEach((filter) => {
      const selectedBrands = filter.getSelectedBrands();
      if (selectedBrands.length > 0) {
        activeFilters.push({
          filterValue: selectedBrands,
          type: "brand",
        });
      }
    });

    // Process workshop filters (options)
    allWorkshopFilters.forEach((filter) => {
      const activeOption = filter.querySelector(
        ".workshop-filter__option.active"
      );
      if (!activeOption) return;

      const metafieldKey = filter.getAttribute("data-metafield-key");
      const filterValue = activeOption.getAttribute("data-value");

      if (filterValue && filterValue !== "") {
        activeFilters.push({
          metafieldKey: metafieldKey,
          filterValue: filterValue,
          type: "metafield",
        });
      }
    });

    // Separate products into filtered and other products
    const filteredProducts = [];
    const otherProducts = [];

    productItems.forEach((item) => {
      let isFiltered = false;

      // If no filters are active, all products are considered "other"
      if (activeFilters.length === 0) {
        otherProducts.push(item);
      } else {
        // Check if product matches ANY of the active filters (OR logic)
        for (let filter of activeFilters) {
          if (filter.type === "metafield") {
            const productMetafieldValue = item.getAttribute(
              `data-${filter.metafieldKey.replace(/_/g, "-")}`
            );

            // If product matches this filter, it's filtered
            if (productMetafieldValue === filter.filterValue) {
              isFiltered = true;
              break;
            }
          }
        }

        if (isFiltered) {
          filteredProducts.push(item);
        } else {
          otherProducts.push(item);
        }
      }
    });

    // Create a container for the new structure
    const container = document.createElement("div");
    container.className = "filtered-products-container";

    // Add filtered products section if there are filtered products
    if (filteredProducts.length > 0) {
      const filteredSection = document.createElement("div");
      filteredSection.className = "filtered-products-section";
      filteredSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <ul class="filtered-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const filteredGrid = filteredSection.querySelector(
        ".filtered-products-grid"
      );
      filteredProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        filteredGrid.appendChild(item);
      });

      container.appendChild(filteredSection);
    } else if (activeFilters.length > 0) {
      // Show no results message when filters are active but no products match
      const noResultsSection = document.createElement("div");
      noResultsSection.className =
        "filtered-products-section no-results-section";
      noResultsSection.innerHTML = `
        <div class="filtered-products-header">
          <h3 class="filtered-products-title">FILTERED PRODUCTS</h3>
        </div>
        <div class="no-results-message" style="text-align: center; padding: 2rem 1rem; color: #666;">
          No products found matching your filters
        </div>
      `;

      container.appendChild(noResultsSection);
    }

    // Add other products section
    // Only show for: non-brand collections OR KINTO (hide for all other brands)
    const currentCollection = CollectionTypeHelper.getCurrentCollection();
    const shouldShowOtherProducts =
      !CollectionTypeHelper.isBrandCollection() ||
      currentCollection === "kinto";

    if (otherProducts.length > 0 && shouldShowOtherProducts) {
      const otherSection = document.createElement("div");
      otherSection.className = "other-products-section";
      otherSection.innerHTML = `
        <div class="other-products-header">
          <h3 class="other-products-title">PRODUCTS</h3>
        </div>
        <ul class="other-products-grid grid product-grid grid--2-col-tablet-down grid--2-col-desktop">
        </ul>
      `;

      const otherGrid = otherSection.querySelector(".other-products-grid");
      otherProducts.forEach((item) => {
        // Remove any existing classes and reset display
        item.classList.remove("custom-filter-hidden");
        item.style.display = "";
        otherGrid.appendChild(item);
      });

      container.appendChild(otherSection);
    }

    // Replace the product grid content
    productGrid.innerHTML = "";
    productGrid.appendChild(container);

    // Hide empty state since we're always showing products
    this.toggleEmptyState(false);
  }

  toggleEmptyState(isEmpty) {
    const productGrid = document.getElementById("product-grid");
    const collection = productGrid?.closest(".collection");

    if (!collection) return;

    let emptyState = collection.querySelector(".custom-filter-empty-state");

    if (isEmpty) {
      if (!emptyState) {
        emptyState = document.createElement("div");
        emptyState.className = "custom-filter-empty-state";
        emptyState.innerHTML = `
          <div style="text-align: center; padding: 2rem 1rem; color: #666;">
            No results found
          </div>
        `;
        collection.appendChild(emptyState);
      }
      emptyState.style.display = "block";
    } else {
      if (emptyState) {
        emptyState.style.display = "none";
      }
    }
  }

  updateProductCount() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const allProducts = productGrid.querySelectorAll(
      ".grid__item[data-product-id]"
    );
    const filteredProducts = productGrid.querySelectorAll(
      ".filtered-products-grid .grid__item[data-product-id]"
    );
    const otherProducts = productGrid.querySelectorAll(
      ".other-products-grid .grid__item[data-product-id]"
    );

    // Update product count displays to show total products
    const countElements = document.querySelectorAll(
      "#ProductCount, #ProductCountDesktop"
    );
    countElements.forEach((element) => {
      if (element) {
        const currentText = element.textContent;
        const newText = currentText.replace(/\d+/, allProducts.length);
        element.textContent = newText;
      }
    });

    // Remove any existing filter info
    document.querySelectorAll(".filter-info").forEach((info) => info.remove());
  }

  updateURL(metafieldKey, filterValue) {
    const url = new URL(window.location);
    const paramName = `custom_filter_${metafieldKey}`;

    if (filterValue && filterValue !== "") {
      url.searchParams.set(paramName, filterValue);
    } else {
      url.searchParams.delete(paramName);
    }

    // Update URL without page refresh
    window.history.replaceState({}, "", url.toString());
  }

  initializeFromURL() {
    // Skip URL initialization if we're in navigation mode (not coffee/equipment collection)
    if (!CollectionTypeHelper.shouldUseClientSideFiltering()) {
      return;
    }

    const url = new URL(window.location);
    const metafieldKey =
      this.getAttribute("data-metafield-key") || "workshop_type";
    const paramName = `custom_filter_${metafieldKey}`;
    const currentValue = url.searchParams.get(paramName);

    if (currentValue) {
      this.updateOptionSelection(currentValue);
      // Apply filter immediately - no setTimeout needed
      this.applyFilter(currentValue);
    }
  }
}

// Custom element definitions
customElements.define("custom-filter", CustomFilter);
customElements.define("equipment-filter", EquipmentFilter);
customElements.define("brand-filter", BrandFilter);
customElements.define("workshop-filter", WorkshopFilter);

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Small delay to ensure all elements are ready
  setTimeout(() => {
    // Check if we're on a collection page before initializing
    if (
      CollectionTypeHelper.getCurrentCollection() &&
      !window.customFiltersInitialized
    ) {
      console.log(
        "Initializing filters for collection:",
        CollectionTypeHelper.getCurrentCollection()
      );
      try {
        CustomFilter.initializeAll();
      } catch (error) {
        console.error("Error initializing filters:", error);
        // Don't let filter errors break the page
      }
    } else {
      console.log(
        "Not on a collection page or already initialized, skipping filter initialization"
      );
    }
  }, 100); // Reduced delay - filters initialize faster

  // Add event listener for mobile reset all button
  const mobileResetAllBtn = document.querySelector(
    "[data-reset-all-filters-mobile]"
  );
  if (mobileResetAllBtn) {
    mobileResetAllBtn.addEventListener("click", (event) => {
      event.preventDefault();
      // Reset custom filters (sliders)
      const allCustomFilters = document.querySelectorAll("custom-filter");
      allCustomFilters.forEach((filter) => {
        const slider =
          filter.querySelector(".custom-filter__slider") ||
          filter.querySelector(".mobile-custom-filter__slider");
        if (slider) {
          slider.value = 0;
          filter.updateDisplay(0);
          filter.updateFlavorProfileIcon(0); // Update icon first to show empty state
          filter.applyFilter(0);
        }
      });

      // Reset equipment filters (options and clickable titles)
      const allEquipmentFilters = document.querySelectorAll("equipment-filter");
      allEquipmentFilters.forEach((filter) => {
        // Check if this is a clickable title filter
        const clickableTitle = filter.querySelector(
          ".equipment-filter__title--clickable"
        );
        if (clickableTitle) {
          filter.updateTitleSelection("");
          filter.applyFilter("");
        } else if (filter.updateOptionSelection) {
          filter.updateOptionSelection("");
          filter.applyFilter("");
        }
      });

      // Reset brand filters (clickable options)
      const allBrandFilters = document.querySelectorAll("brand-filter");
      allBrandFilters.forEach((filter) => {
        filter.updateOptionSelection("");
        filter.applyFilter();
      });

      // Reset workshop filters (options)
      const allWorkshopFilters = document.querySelectorAll("workshop-filter");
      allWorkshopFilters.forEach((filter) => {
        if (filter.updateOptionSelection) {
          filter.updateOptionSelection("");
          filter.applyFilter("");
        }
      });
    });
  }

  // Add event listener for desktop reset all button
  const desktopResetAllBtn = document.querySelector("[data-reset-all-filters]");
  if (desktopResetAllBtn) {
    desktopResetAllBtn.addEventListener("click", (event) => {
      event.preventDefault();
      // Reset custom filters (sliders)
      const allCustomFilters = document.querySelectorAll("custom-filter");
      allCustomFilters.forEach((filter) => {
        const slider =
          filter.querySelector(".custom-filter__slider") ||
          filter.querySelector(".mobile-custom-filter__slider");
        if (slider) {
          slider.value = 0;
          filter.updateDisplay(0);
          filter.updateFlavorProfileIcon(0); // Update icon first to show empty state
          filter.applyFilter(0);
        }
      });

      // Reset equipment filters (options and clickable titles)
      const allEquipmentFilters = document.querySelectorAll("equipment-filter");
      allEquipmentFilters.forEach((filter) => {
        // Check if this is a clickable title filter
        const clickableTitle = filter.querySelector(
          ".equipment-filter__title--clickable"
        );
        if (clickableTitle) {
          filter.updateTitleSelection("");
          filter.applyFilter("");
        } else if (filter.updateOptionSelection) {
          filter.updateOptionSelection("");
          filter.applyFilter("");
        }
      });

      // Reset brand filters (clickable options)
      const allBrandFilters = document.querySelectorAll("brand-filter");
      allBrandFilters.forEach((filter) => {
        filter.updateOptionSelection("");
        filter.applyFilter();
      });

      // Reset workshop filters (options)
      const allWorkshopFilters = document.querySelectorAll("workshop-filter");
      allWorkshopFilters.forEach((filter) => {
        if (filter.updateOptionSelection) {
          filter.updateOptionSelection("");
          filter.applyFilter("");
        }
      });
    });
  }
});

// Re-initialize after any dynamic content changes (but be more selective)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
      // Check if product grid was updated
      const hasProductGrid = Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === 1 &&
          (node.id === "product-grid" ||
            (node.querySelector && node.querySelector("#product-grid")))
      );

      // Only reinitialize if we don't already have filters initialized
      if (hasProductGrid && !window.customFiltersInitialized) {
        setTimeout(() => {
          try {
            CustomFilter.initializeAll();
          } catch (error) {
            console.error("Error reinitializing filters:", error);
          }
        }, 100);
      }
    }
  });
});

// Observe changes to the main content area
const mainContent =
  document.getElementById("ProductGridContainer") || document.body;
observer.observe(mainContent, {
  childList: true,
  subtree: true,
});

// Recovery function to restore products if they disappear
function restoreProducts() {
  const productGrid = document.getElementById("product-grid");
  if (!productGrid || !window.originalProducts) return;

  const currentProducts = productGrid.querySelectorAll(
    ".grid__item[data-product-id]"
  );
  if (currentProducts.length === 0 && window.originalProducts.length > 0) {
    console.log("No products found, restoring from backup...");
    productGrid.innerHTML = window.originalProducts.join("");
    // Clear the backup after restoring to prevent conflicts
    window.originalProducts = null;
  }
}

// Global error handler to prevent products from disappearing
window.addEventListener("error", function (event) {
  console.error("Global error caught:", event.error);
  // If there's a jQuery error, try to reinitialize filters after a delay
  if (
    event.error &&
    event.error.message &&
    event.error.message.includes("jQuery")
  ) {
    console.log("jQuery error detected, attempting to recover...");
    setTimeout(() => {
      restoreProducts();
      if (window.customFiltersInitialized) {
        window.customFiltersInitialized = false;
        CustomFilter.initializeAll();
      }
    }, 1000);
  }
});

// Monitor for disappearing products (disabled to prevent conflicts)
// setInterval(restoreProducts, 5000);

// Disable the original facet system integration for custom filters
// (Keep the existing facet system working for other filters)
const currentCollection = CollectionTypeHelper.getCurrentCollection();
const isCoffeeOrEquipment =
  CollectionTypeHelper.isCoffeeOrEquipmentCollection();
const isBrand = CollectionTypeHelper.isBrandCollection();

console.log("Custom Filter System Debug:", {
  currentCollection: currentCollection,
  isCoffeeOrEquipment: isCoffeeOrEquipment,
  isBrand: isBrand,
  shouldUseClientSideFiltering:
    CollectionTypeHelper.shouldUseClientSideFiltering(),
  shouldHideOtherProducts:
    CollectionTypeHelper.shouldHideOtherProductsSection(),
});

if (CollectionTypeHelper.shouldUseClientSideFiltering()) {
  console.log(
    "Custom Filter System: Client-side filtering enabled for coffee/equipment collection"
  );
} else {
  console.log(
    "Custom Filter System: Navigation mode enabled (filters will navigate to collections)"
  );
}
